/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import Loader from '@mdsol/lego/lib/Loader'
import { useServiceContext } from '~/root'
import { useModal } from '~/contexts/ModalContext'
import Modal from '~/components/Modal'
import DocState from '../shared/DocState'
import TextLinkButton from '../shared/TextLinkButton'
import { WorkflowType, TASK_ACTIONS } from '~/constants/workflow'
import { getById } from '~/services/api/workflows'
import { getFsiPath } from '~/services/api/files'
import WfService from '~/lib/WfService'

interface ParticipatingFsisModalProps {
  wfId: number
  show: boolean
  onClose: () => void
  showDocumentsModalOnTop?: boolean
}

interface WorkflowFile {
  draftFsi?: any
  targetFsi?: any
  sourceFsi?: any
}

interface Workflow {
  id: number
  workflowType: string
  parentFolderId?: number
  files: WorkflowFile[]
}

// Utility function to truncate text with ellipsis
const excerpt = (text: string, maxLength: number): string => {
  if (!text) return ''
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const ParticipatingFsisModal: React.FC<ParticipatingFsisModalProps> = ({
  wfId,
  show,
  onClose,
  showDocumentsModalOnTop
}) => {
  const context = useServiceContext()
  const navigate = useNavigate()
  const { hideAllModals } = useModal()
  const { translate } = context.service.filter
  const [loading, setLoading] = useState<boolean>(false)
  const [workflow, setWorkflow] = useState<Workflow | null>(null)
  const [fileFullPath, setFileFullPath] = useState<string | null>(null)

  useEffect(() => {
    if (!show || !wfId) return

    setLoading(true)
    getById(wfId)
      .then((wfData) => {
        setWorkflow(wfData as any)

        if (wfData?.parentFolderId) {
          getFsiPath(wfData.parentFolderId)
            .then((data) => {
              const path = data.reduce((memo, item) => (memo ? memo + '/' : '') + item.name, '') + '/'
              setFileFullPath(path)
              setLoading(false)
            })
            .catch(() => {
              setLoading(false)
            })
        } else {
          setLoading(false)
        }
      })
      .catch(() => {
        // open error modal ??
        console.error('WF ' + wfId + ' not loaded')
        setLoading(false)
      })
  }, [wfId, show])

  const handleLinkClick = (workingFile: any) => {
    const routePath =
      workflow?.workflowType === WorkflowType.QC_REVIEW
        ? `/show-file-qc/${workingFile.id}`
        : `/show-file/${workingFile.id}`

    hideAllModals()
    navigate(`${routePath}?action=taskComplete`)
  }

  return (
    <Modal
      show={show}
      handleClose={onClose}
      title={translate('DOCUMENTS')}
      size="md"
      isConfirmationDialog={showDocumentsModalOnTop}
    >
      <Loader blocking={loading}>
        <Modal.Body>
          <label className="mb0">{translate('DOCUMENTS')}</label>
          <div className="info" style={{ maxHeight: '495px', overflowY: 'auto' }}>
            {workflow &&
              workflow.files &&
              workflow.files.map((file) => {
                const workingFile = WfService.getWorkingFsi(file)
                return workingFile ? (
                  <div key={workingFile.id}>
                    {workingFile?.parentId ? (
                      <TextLinkButton
                        action={() => {
                          hideAllModals()
                          navigate(`/show-file/${workingFile.id}?action=taskComplete`)
                        }}
                        className="trunk-btn-link"
                        style={{ padding: 0 }}
                      >
                        {excerpt(workingFile.name, 164)}
                      </TextLinkButton>
                    ) : (
                      <span>{workingFile?.name}</span>
                    )}
                    <DocState fsi={workingFile} />
                  </div>
                ) : null
              })}
          </div>
        </Modal.Body>
      </Loader>
      <Modal.Footer>
        <Modal.Footer.Button onClick={onClose} variant="primary">
          {translate('OK')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ParticipatingFsisModal
